import { Controller } from "@hotwired/stimulus";

// Behaviours for the AppUi::MultiSelect popover:
//   - Live-filter visible options as the user types.
//   - Keep the button label and badge count in sync with checked state.
//   - Provide a "Clear" action that unchecks everything.
//   - Dispatch a `change` event on each checkbox change so a parent
//     frame-filter form picks the update up.
export default class extends Controller {
  static targets = ["search", "option", "list", "buttonLabel", "badge", "count", "empty"];
  static values = { name: String };

  filter() {
    const term = (this.searchTarget.value || "").trim().toLowerCase();
    let visible = 0;

    this.optionTargets.forEach((opt) => {
      const label = opt.dataset.label || "";
      const match = !term || label.includes(term);
      opt.classList.toggle("hidden", !match);
      if (match) visible++;
    });

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visible > 0);
    }
  }

  changed() {
    this.refreshLabel();
  }

  clear() {
    this.checkboxes.forEach((cb) => {
      if (!cb.checked) return;
      cb.checked = false;
      cb.dispatchEvent(new Event("change", { bubbles: true }));
    });
    this.refreshLabel();
  }

  refreshLabel() {
    const checked = this.checkboxes.filter((cb) => cb.checked);
    const count = checked.length;

    if (this.hasButtonLabelTarget) {
      let text;
      if (count === 0) {
        text = this.buttonLabelTarget.dataset.placeholder || this.placeholderFallback();
        this.buttonLabelTarget.classList.add("text-gray-400");
        this.buttonLabelTarget.classList.remove("text-gray-900");
      } else if (count === 1) {
        const labelEl = checked[0].closest("label")?.querySelector("span");
        text = labelEl ? labelEl.textContent.trim() : `${count} selected`;
        this.buttonLabelTarget.classList.remove("text-gray-400");
        this.buttonLabelTarget.classList.add("text-gray-900");
      } else {
        text = `${count} selected`;
        this.buttonLabelTarget.classList.remove("text-gray-400");
        this.buttonLabelTarget.classList.add("text-gray-900");
      }
      this.buttonLabelTarget.textContent = text;
    }

    if (this.hasBadgeTarget) {
      if (count > 0) {
        this.badgeTarget.textContent = String(count);
        this.badgeTarget.classList.remove("hidden");
      } else {
        this.badgeTarget.classList.add("hidden");
      }
    }

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${count} selected`;
    }
  }

  get checkboxes() {
    return this.optionTargets.map((opt) => opt.querySelector("input[type='checkbox']")).filter(Boolean);
  }

  placeholderFallback() {
    // Try to recover the original placeholder from the rendered text on first paint.
    return this.buttonLabelTarget.textContent.trim();
  }
}
